import {
	ReactElement,
	useCallback,
	useContext
} from "react";
import { AppType } from "client/Api/Apps/common";
import { AppTypeSpecificTileSettings } from "../../RenderState/common";
import { InteractionState, TileContext } from "../TileContext";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { LiveTileContent } from "../LiveTileContent";
import { never } from "client_legacy/ndaw/errors";
import { isNotNil } from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { IntegrationFlowKind } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";
import { GeneralLiveTileContent } from "./General/GeneralLiveTileContent";
import { PlanningLiveTileContent } from "./Planning/PlanningLiveTileContent";

export type IntegrationTileSettings = AppTypeSpecificTileSettings<AppType.Integration>;

export type IntegrationLiveTileContentProps = {
	settings: IntegrationTileSettings;
	onNavigation: () => void;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const IntegrationLiveTileContent = (props: IntegrationLiveTileContentProps): ReactElement => {
	const {
		settings,
		onNavigation,
		userTimezone,
		onOpenTileMenu
	} = props;

	const {
		icon,
		name,
		environmentAppId,
		conditionalAccess,
		maintenanceWindow,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		conditionalAccess,
		maintenanceWindow,
		navigationHandler: onNavigation,
		appIsLicensed,
		appIsNotAccessible
	});

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigation();
		},
		[addAnalyticsEvent, onNavigation]
	);

	const isUnavailable: boolean = isNotNil(maintenanceWindow)
		|| isNotNil(conditionalAccess)
		|| interactionState === InteractionState.Disabled;

	if (isNotNil(maintenanceWindow) || isNotNil(conditionalAccess)) {
		return (
			<LiveTileContent
				icon={icon}
				name={name}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
				overlay={overlayState}
			/>
		);
	}

	const {
		connectionId,
		hideHeader,
		hideFooter,
		navigationUrl,
		flowSettings
	} = settings;

	const renderContent = (): ReactElement => {
		switch (flowSettings.kind) {
			case IntegrationFlowKind.General:
				return (
					<GeneralLiveTileContent
						connectionId={connectionId}
						appName={name}
						userTimezone={userTimezone}
					/>
				);
			case IntegrationFlowKind.Planning:
				return (
					<PlanningLiveTileContent
						connectionId={connectionId}
						appName={name}
						dimensionToRender={dimension}
						flowSettings={flowSettings}
						userTimezone={userTimezone}
					/>
				);
			default:
				return never(flowSettings);
		}
	};

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			{!hideHeader && (
				<LiveTileContent.Header icon={icon}>
					<LiveTileContent.Title text={name} />
				</LiveTileContent.Header>
			)}

			<LiveTileContent.Content>
				{!isUnavailable && renderContent()}
			</LiveTileContent.Content>

			{navigationUrl && !hideFooter && (
				<LiveTileContent.Footer
					name={name}
					onClick={navigateHandler}
				/>
			)}
		</LiveTileContent>
	);
};
IntegrationLiveTileContent.displayName = "IntegrationLiveTileContent";
